<script setup lang="ts">
import type { PropertyFeatureSelectProps } from '.';

import { FeatureEnum, featuresTranslations } from '~/core/entities/CRMEnums';

import { DefaultFilterSelect } from '../DefaultFilterSelect/';
import messages from './PropertyFeatureSelect.locale.json';

const strings = messages.pt;

const props = withDefaults(defineProps<PropertyFeatureSelectProps>(), {
	variant: 'primary'
});

const selectedPropertyTypes = defineModel<FeatureEnum[]>({
	required: true,
	default: () => []
});

const modelValue = ref<{ value: FeatureEnum; label: string }[]>([]);

const items = computed<{ value: FeatureEnum; label: string }[]>(() => {
	const features = Object.keys(featuresTranslations) as FeatureEnum[];

	return features
		.map(featureKey => ({
			value: featureKey,
			label: featuresTranslations[featureKey]
		}))
		.sort((featureA, featureB) => {
			const featureATranslation = featuresTranslations[featureA.value];
			const featureBTranslation = featuresTranslations[featureB.value];

			return featureATranslation.localeCompare(featureBTranslation);
		});
});

const highlightedItems = computed<{ value: FeatureEnum; label: string }[]>(() =>
	[
		{
			value: FeatureEnum.PRIVATE_POOL,
			label: featuresTranslations[FeatureEnum.PRIVATE_POOL]
		},
		{
			value: FeatureEnum.GOURMET_TERRACE,
			label: featuresTranslations[FeatureEnum.GOURMET_TERRACE]
		},
		{ value: FeatureEnum.GYM, label: featuresTranslations[FeatureEnum.GYM] },
		{
			value: FeatureEnum.AIR_CONDITIONING,
			label: featuresTranslations[FeatureEnum.AIR_CONDITIONING]
		}
	].filter(
		highlighted => !selectedPropertyTypes.value.includes(highlighted.value)
	)
);

function getItemLabel({
	label
}: {
	value: FeatureEnum;
	label: string;
}): string {
	return label;
}

function filter(
	items: {
		value: FeatureEnum;
		label: string;
	}[],
	term: string
): {
	value: FeatureEnum;
	label: string;
}[] {
	const normalizedTerm = normalizeString(term);

	return items.filter(item => {
		const normalizedItem = normalizeString(item.label);

		return normalizedItem.includes(normalizedTerm);
	});
}

function updateModelValue(newValues: any) {
	modelValue.value = newValues.map(toRaw);
	selectedPropertyTypes.value = modelValue.value.map(({ value }) => value);
}

watch(
	selectedPropertyTypes,
	() => {
		const newValue = selectedPropertyTypes.value.map(value => ({
			value,
			label: featuresTranslations[value]
		}));

		if (newValue === modelValue.value) return;

		modelValue.value = newValue;
	},
	{ immediate: true }
);
</script>

<template>
	<DefaultFilterSelect
		:model-value="modelValue"
		:title="strings.title"
		:items="items"
		:highlighted-items="highlightedItems"
		:get-item-label="getItemLabel"
		:filter-function="filter"
		:separator-variant="separatorVariant"
		:variant="props.variant"
		@update:model-value="updateModelValue"
	/>
</template>
